import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BlogBackToTopButton from '../components/BlogBackToTopButton';
import { API_BASE_URL, apiUrl } from '../config';

function resolveBlogImageUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/uploads/') || raw.startsWith('/api/uploads/')) return `${API_BASE_URL}${raw}`;
  if (raw.startsWith('uploads/')) return `${API_BASE_URL}/${raw}`;
  return raw;
}

function setMetaTags({ title, description, canonicalUrl, ogImageUrl }) {
  const absoluteOgImageUrl = resolveBlogImageUrl(ogImageUrl);
  document.title = title;

  const ensureMeta = (selector, attrs) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      Object.entries(attrs.match || {}).forEach(([key, value]) => el.setAttribute(key, value));
      document.head.appendChild(el);
    }
    Object.entries(attrs.set || {}).forEach(([key, value]) => el.setAttribute(key, value));
    return el;
  };

  const ensureLink = (selector, rel, href) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
    return el;
  };

  ensureMeta('meta[name="description"]', {
    match: { name: 'description' },
    set: { content: description || '' }
  });
  ensureMeta('meta[property="og:title"]', {
    match: { property: 'og:title' },
    set: { content: title }
  });
  ensureMeta('meta[property="og:description"]', {
    match: { property: 'og:description' },
    set: { content: description || '' }
  });
  ensureMeta('meta[property="og:type"]', {
    match: { property: 'og:type' },
    set: { content: 'website' }
  });
  ensureMeta('meta[property="og:url"]', {
    match: { property: 'og:url' },
    set: { content: canonicalUrl }
  });
  ensureMeta('meta[property="og:image"]', {
    match: { property: 'og:image' },
    set: { content: absoluteOgImageUrl || '' }
  });
  ensureMeta('meta[name="twitter:card"]', {
    match: { name: 'twitter:card' },
    set: { content: absoluteOgImageUrl ? 'summary_large_image' : 'summary' }
  });
  ensureMeta('meta[name="twitter:title"]', {
    match: { name: 'twitter:title' },
    set: { content: title }
  });
  ensureMeta('meta[name="twitter:description"]', {
    match: { name: 'twitter:description' },
    set: { content: description || '' }
  });
  ensureMeta('meta[name="twitter:image"]', {
    match: { name: 'twitter:image' },
    set: { content: absoluteOgImageUrl || '' }
  });
  ensureLink('link[rel="canonical"]', 'canonical', canonicalUrl);
}

function buildCategoryJsonLd(category, featuredPost) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category?.seoTitle || category?.name,
    description: category?.seoDescription || category?.description,
    url: `${window.location.origin}${category?.canonicalUrl || ''}`,
    hasPart: featuredPost ? [{
      '@type': 'Article',
      headline: featuredPost.seoMetaTitle || featuredPost.title,
      url: `${window.location.origin}${featuredPost.canonicalUrl || ''}`
    }] : undefined
  };
}

function buildBreadcrumbJsonLd(category) {
  const canonicalUrl = `${window.location.origin}${category?.canonicalUrl || ''}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Anasayfa',
        item: window.location.origin
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${window.location.origin}/blog`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: category?.name || 'Kategori',
        item: canonicalUrl
      }
    ]
  };
}

export default function BlogCategoryPage({ isDarkMode, toggleDarkMode }) {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState({
    category: null,
    featuredPost: null,
    featuredPosts: [],
    posts: [],
    supportingPosts: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await fetch(apiUrl(`/api/blog/categories/${categorySlug}`));
        const data = await res.json().catch(() => ({}));

        if (res.ok) {
          if (!mounted) return;
          setPayload({
            category: data?.category || null,
            featuredPost: data?.featuredPost || null,
            featuredPosts: Array.isArray(data?.featuredPosts) ? data.featuredPosts : [],
            posts: Array.isArray(data?.posts) ? data.posts : [],
            supportingPosts: Array.isArray(data?.supportingPosts) ? data.supportingPosts : []
          });
          return;
        }

        if (res.status === 404) {
          const resolveRes = await fetch(apiUrl(`/api/blog/resolve/${categorySlug}`));
          const resolveData = await resolveRes.json().catch(() => ({}));

          if (!mounted) return;

          if (resolveRes.ok && resolveData?.type === 'post' && resolveData?.redirectPath) {
            navigate(resolveData.redirectPath, { replace: true });
            return;
          }

          setError('Kategori bulunamadı.');
          setLoading(false);
          return;
        }

        throw new Error(data?.error || 'blog_category_failed');
      } catch (_error) {
        if (!mounted) return;
        setError('Kategori içeriği şu anda yüklenemedi.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [categorySlug, navigate]);

  const category = payload.category;

  const jsonLdPayloads = useMemo(() => {
    if (!category) return [];
    return [
      buildCategoryJsonLd(category, payload.featuredPost),
      buildBreadcrumbJsonLd(category)
    ];
  }, [category, payload.featuredPost]);

  useEffect(() => {
    if (!category) return undefined;

    const canonicalUrl = `${window.location.origin}${category.canonicalUrl || `/blog/${categorySlug}`}`;
    setMetaTags({
      title: `${category.seoTitle || category.name} | BBTV Blog`,
      description: category.seoDescription || category.description || '',
      canonicalUrl,
      ogImageUrl: payload.featuredPost?.ogImageUrl || payload.featuredPost?.coverImageUrl || ''
    });

    const existingScripts = Array.from(document.querySelectorAll('[data-blog-jsonld="true"]'));
    existingScripts.forEach((script) => script.parentNode?.removeChild(script));

    jsonLdPayloads.forEach((jsonLd, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.blogJsonld = 'true';
      script.dataset.blogJsonldIndex = String(index);
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    });

    return () => {
      const latestScripts = Array.from(document.querySelectorAll('[data-blog-jsonld="true"]'));
      latestScripts.forEach((script) => script.parentNode?.removeChild(script));
    };
  }, [category, categorySlug, jsonLdPayloads, payload.featuredPost]);

  return (
    <div className="min-h-screen bg-white text-slate-900 dark:bg-[#1a1a1a] dark:text-white">
      <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      <main className="mx-auto max-w-[1320px] px-6 pb-20 pt-24 md:px-8 md:pt-28 xl:px-10">
        {loading ? (
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-sm dark:border-slate-800 dark:bg-slate-900/60">
            Kategori yükleniyor...
          </section>
        ) : error || !category ? (
          <section className="rounded-xl border border-red-200 bg-red-50 p-8 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-300">
            {error || 'Kategori bulunamadı.'}
          </section>
        ) : (
          <>
            <nav className="mb-6 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <Link to="/" className="hover:text-primary">Anasayfa</Link>
              <span className="material-symbols-outlined text-sm">chevron_right</span>
              <Link to="/blog" className="hover:text-primary">Blog</Link>
              <span className="material-symbols-outlined text-sm">chevron_right</span>
              <span>{category.name}</span>
            </nav>

            <section className="rounded-[2rem] border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 dark:border-slate-800 dark:from-slate-950 dark:to-slate-900">
              <div className="max-w-5xl">
                <p className="mb-3 text-[11px] font-black uppercase tracking-[0.28em] text-primary">Kategori Landing</p>
                <h1 className="font-serif text-4xl font-black tracking-tight text-slate-950 dark:text-white md:text-6xl">
                  {category.name}
                </h1>
                <p className="mt-5 max-w-4xl text-base leading-8 text-slate-600 dark:text-slate-300">
                  {category.introContent || category.description}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/kayit-ol"
                    className="rounded-xl bg-primary px-5 py-3 text-sm font-black text-white transition hover:scale-[0.99]"
                  >
                    Kayıt Ol
                  </Link>
                  <Link
                    to="/blog"
                    className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Tüm Blog
                  </Link>
                </div>
              </div>
            </section>

            {payload.featuredPost ? (
              <section className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
                <Link to={payload.featuredPost.canonicalUrl} className="overflow-hidden rounded-[1.5rem]">
                  <div className="h-[360px] overflow-hidden rounded-[1.5rem] bg-slate-100 dark:bg-slate-800">
                    {payload.featuredPost.coverImageUrl ? (
                      <img
                        src={resolveBlogImageUrl(payload.featuredPost.coverImageUrl)}
                        alt={payload.featuredPost.coverImageAlt || payload.featuredPost.title}
                        className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-slate-400">Kapak görseli</div>
                    )}
                  </div>
                </Link>
                <div className="flex flex-col justify-center rounded-[1.5rem] border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
                  <div className="text-[11px] font-black uppercase tracking-[0.28em] text-primary">Öne Çıkan İçerik</div>
                  <h2 className="mt-3 font-serif text-3xl font-black leading-tight tracking-tight text-slate-950 dark:text-white">
                    {payload.featuredPost.title}
                  </h2>
                  <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                    {payload.featuredPost.excerpt}
                  </p>
                  <Link
                    to={payload.featuredPost.canonicalUrl}
                    className="mt-6 inline-flex w-fit items-center gap-2 text-sm font-black text-primary"
                  >
                    İçeriği Oku
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </Link>
                </div>
              </section>
            ) : null}

            <section className="mt-10">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-serif text-3xl font-black tracking-tight text-slate-950 dark:text-white">Kategori İçerikleri</h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    {category.name} başlığındaki tüm içerikler tek yerde.
                  </p>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {(payload.supportingPosts.length ? payload.supportingPosts : payload.posts).map((post) => (
                  <Link
                    key={post.id}
                    to={post.canonicalUrl}
                    className="rounded-[1.5rem] border border-slate-200 bg-white p-5 transition hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-950"
                  >
                    <div className="text-[11px] font-black uppercase tracking-[0.18em] text-primary">{category.name}</div>
                    <h3 className="mt-3 font-serif text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
                      {post.title}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {post.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </section>

            {(category.faqItems || []).length > 0 ? (
              <section className="mt-16 rounded-[1.5rem] border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
                <h2 className="font-serif text-3xl font-black tracking-tight text-slate-950 dark:text-white">Kısa Sorular</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  {category.faqItems.map((item, index) => (
                    <div key={`${item.question}-${index}`} className="rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
                      <div className="text-sm font-black text-slate-950 dark:text-white">{item.question}</div>
                      <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}
      </main>

      <BlogBackToTopButton />
      <Footer />
    </div>
  );
}
